import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { mergeProgress, toSyncProgress, type SyncProgress } from './progressSync';
import { createProgressRepository, PROGRESS_STORAGE_KEY } from './progressRepository';

type Theme = 'light' | 'dark';

interface StoredState {
  bookmarkedCases: string[];
  bookmarkedNodes: string[];
  masteredNodes: string[];
  notes: Record<string, string>;
  noteUpdatedAt: Record<string, string>;
  recentCases: string[];
  viewedAt: Record<string, string>;
  completedSteps: string[];
  openedCases: string[];
  viewedCases: string[];
  skippedCases: string[];
  discoveryQueue: string[];
  lastHomeCase: string;
  theme: Theme;
  preferencesUpdatedAt: string;
}

interface AtlasStore extends StoredState {
  toggleCaseBookmark: (caseId: string) => void;
  toggleNodeBookmark: (nodeId: string) => void;
  toggleMastered: (nodeId: string) => void;
  setNote: (nodeId: string, text: string) => void;
  markVisited: (caseId: string) => void;
  markCaseViewed: (caseId: string) => void;
  recordSkipped: (caseId: string) => void;
  setDiscoveryQueue: (caseIds: string[]) => void;
  setLastHomeCase: (caseId: string) => void;
  toggleStep: (stepKey: string) => void;
  toggleTheme: () => void;
  applySyncedProgress: (remote: SyncProgress) => void;
  importData: (data: unknown) => boolean;
  reset: () => void;
}

const repository = createProgressRepository();

const defaultState: StoredState = {
  bookmarkedCases: [],
  bookmarkedNodes: [],
  masteredNodes: [],
  notes: {},
  noteUpdatedAt: {},
  recentCases: [],
  viewedAt: {},
  completedSteps: [],
  openedCases: [],
  viewedCases: [],
  skippedCases: [],
  discoveryQueue: [],
  lastHomeCase: '',
  theme: 'light',
  preferencesUpdatedAt: '',
};

const AtlasContext = createContext<AtlasStore | null>(null);

function normalizeDateMap(value: unknown): Record<string, string> {
  const output: Record<string, string> = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) return output;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string' && !Number.isNaN(Date.parse(item))) output[key] = item;
  }
  return output;
}

function normalizeStored(parsed: Partial<StoredState> | null): StoredState {
  return {
    ...defaultState,
    ...parsed,
    notes: parsed?.notes ?? {},
    noteUpdatedAt: normalizeDateMap(parsed?.noteUpdatedAt),
    viewedAt: normalizeDateMap(parsed?.viewedAt),
    openedCases: Array.isArray(parsed?.openedCases) ? parsed.openedCases.filter((id): id is string => typeof id === 'string') : [],
    viewedCases: Array.isArray(parsed?.viewedCases) ? parsed.viewedCases.filter((id): id is string => typeof id === 'string') : [],
    skippedCases: Array.isArray(parsed?.skippedCases) ? parsed.skippedCases.filter((id): id is string => typeof id === 'string') : [],
    discoveryQueue: Array.isArray(parsed?.discoveryQueue) ? parsed.discoveryQueue.filter((id): id is string => typeof id === 'string') : [],
    lastHomeCase: typeof parsed?.lastHomeCase === 'string' ? parsed.lastHomeCase : '',
    theme: parsed?.theme === 'dark' ? 'dark' : 'light',
    preferencesUpdatedAt: typeof parsed?.preferencesUpdatedAt === 'string' ? parsed.preferencesUpdatedAt : '',
  };
}

function readStored(): StoredState {
  const stored = repository.load();
  if (stored && typeof stored === 'object') {
    return normalizeStored(stored as Partial<StoredState>);
  }
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return { ...defaultState, theme: prefersDark ? 'dark' : 'light' };
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export function AtlasProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredState>(readStored);

  useEffect(() => {
    repository.save(state);
  }, [state]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PROGRESS_STORAGE_KEY || !event.newValue) return;
      try {
        setState(normalizeStored(JSON.parse(event.newValue) as Partial<StoredState>));
      } catch {
        // Ignore malformed changes from another tab.
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = state.theme;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    themeMeta?.setAttribute('content', state.theme === 'dark' ? '#111512' : '#f4efe5');
  }, [state.theme]);

  const toggleCaseBookmark = useCallback((caseId: string) => {
    setState((current) => ({ ...current, bookmarkedCases: toggle(current.bookmarkedCases, caseId) }));
  }, []);

  const toggleNodeBookmark = useCallback((nodeId: string) => {
    setState((current) => ({ ...current, bookmarkedNodes: toggle(current.bookmarkedNodes, nodeId) }));
  }, []);

  const toggleMastered = useCallback((nodeId: string) => {
    setState((current) => ({ ...current, masteredNodes: toggle(current.masteredNodes, nodeId) }));
  }, []);

  const setNote = useCallback((nodeId: string, text: string) => {
    setState((current) => ({
      ...current,
      notes: { ...current.notes, [nodeId]: text },
      noteUpdatedAt: { ...current.noteUpdatedAt, [nodeId]: new Date().toISOString() },
    }));
  }, []);

  const markVisited = useCallback((caseId: string) => {
    setState((current) => {
      const now = new Date().toISOString();
      const recentCases = [caseId, ...current.recentCases.filter((id) => id !== caseId)].slice(0, 6);
      const openedCases = [caseId, ...current.openedCases.filter((id) => id !== caseId)].slice(0, 20);
      const viewedAt = current.viewedCases.includes(caseId) ? { ...current.viewedAt, [caseId]: now } : current.viewedAt;
      return { ...current, recentCases, openedCases, viewedAt };
    });
  }, []);

  const markCaseViewed = useCallback((caseId: string) => {
    setState((current) => {
      const now = new Date().toISOString();
      const recentCases = [caseId, ...current.recentCases.filter((id) => id !== caseId)].slice(0, 6);
      const openedCases = [caseId, ...current.openedCases.filter((id) => id !== caseId)].slice(0, 20);
      const viewedCases = [caseId, ...current.viewedCases.filter((id) => id !== caseId)].slice(0, 100);
      return { ...current, recentCases, openedCases, viewedCases, viewedAt: { ...current.viewedAt, [caseId]: now } };
    });
  }, []);

  const recordSkipped = useCallback((caseId: string) => {
    setState((current) => ({
      ...current,
      skippedCases: [caseId, ...current.skippedCases.filter((id) => id !== caseId)].slice(0, 12),
    }));
  }, []);

  const setDiscoveryQueue = useCallback((caseIds: string[]) => {
    setState((current) => ({ ...current, discoveryQueue: caseIds }));
  }, []);

  const setLastHomeCase = useCallback((caseId: string) => {
    setState((current) => ({ ...current, lastHomeCase: caseId }));
  }, []);

  const toggleStep = useCallback((stepKey: string) => {
    setState((current) => ({ ...current, completedSteps: toggle(current.completedSteps, stepKey) }));
  }, []);

  const toggleTheme = useCallback(() => {
    setState((current) => ({
      ...current,
      theme: current.theme === 'light' ? 'dark' : 'light',
      preferencesUpdatedAt: new Date().toISOString(),
    }));
  }, []);

  const applySyncedProgress = useCallback((remote: SyncProgress) => {
    setState((current) => {
      const merged = mergeProgress(toSyncProgress(current), remote);
      return {
        ...current,
        ...merged,
        // Session-local exploration choices stay on this device.
        skippedCases: current.skippedCases,
        discoveryQueue: current.discoveryQueue,
        lastHomeCase: current.lastHomeCase,
      };
    });
  }, []);

  const importData = useCallback((data: unknown): boolean => {
    if (!data || typeof data !== 'object') return false;
    const raw = data as Record<string, unknown>;
    const recognized = [
      'bookmarkedCases',
      'bookmarkedNodes',
      'masteredNodes',
      'notes',
      'completedSteps',
      'recentCases',
      'openedCases',
      'viewedCases',
      'viewedAt',
      'skippedCases',
      'discoveryQueue',
    ];
    if (!recognized.some((key) => key in raw)) return false;
    const strings = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);
    const importedNotes: Record<string, string> = {};
    if (raw.notes && typeof raw.notes === 'object' && !Array.isArray(raw.notes)) {
      for (const [key, value] of Object.entries(raw.notes as Record<string, unknown>)) {
        if (typeof value === 'string') importedNotes[key] = value;
      }
    }
    const importedViewedAt: Record<string, string> = {};
    if (raw.viewedAt && typeof raw.viewedAt === 'object' && !Array.isArray(raw.viewedAt)) {
      for (const [key, value] of Object.entries(raw.viewedAt as Record<string, unknown>)) {
        if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) importedViewedAt[key] = value;
      }
    }
    setState((current) => ({
      ...defaultState,
      theme: current.theme,
      bookmarkedCases: strings(raw.bookmarkedCases),
      bookmarkedNodes: strings(raw.bookmarkedNodes),
      masteredNodes: strings(raw.masteredNodes),
      notes: importedNotes,
      recentCases: strings(raw.recentCases),
      viewedAt: importedViewedAt,
      completedSteps: strings(raw.completedSteps),
      openedCases: strings(raw.openedCases),
      viewedCases: strings(raw.viewedCases),
      skippedCases: strings(raw.skippedCases),
      discoveryQueue: strings(raw.discoveryQueue),
      lastHomeCase: typeof raw.lastHomeCase === 'string' ? raw.lastHomeCase : '',
      noteUpdatedAt: normalizeDateMap(raw.noteUpdatedAt),
      preferencesUpdatedAt: typeof raw.preferencesUpdatedAt === 'string' ? raw.preferencesUpdatedAt : '',
    }));
    return true;
  }, []);

  const reset = useCallback(() => {
    setState((current) => ({ ...defaultState, theme: current.theme }));
  }, []);

  const value = useMemo<AtlasStore>(
    () => ({
      ...state,
      toggleCaseBookmark,
      toggleNodeBookmark,
      toggleMastered,
      setNote,
      markVisited,
      markCaseViewed,
      recordSkipped,
      setDiscoveryQueue,
      setLastHomeCase,
      toggleStep,
      toggleTheme,
      applySyncedProgress,
      importData,
      reset,
    }),
    [
      state,
      toggleCaseBookmark,
      toggleNodeBookmark,
      toggleMastered,
      setNote,
      markVisited,
      markCaseViewed,
      recordSkipped,
      setDiscoveryQueue,
      setLastHomeCase,
      toggleStep,
      toggleTheme,
      applySyncedProgress,
      importData,
      reset,
    ],
  );

  return <AtlasContext.Provider value={value}>{children}</AtlasContext.Provider>;
}

export function useAtlas(): AtlasStore {
  const context = useContext(AtlasContext);
  if (!context) throw new Error('useAtlas must be used within AtlasProvider');
  return context;
}
